import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTint, FaBurn, FaFlask, FaWineBottle, FaInfoCircle } from "react-icons/fa";

// ---- Dados dos produtos ----
const PRODUCTS = [
  {
    id: "etanol-hidratado",
    name: "Etanol Hidratado",
    icon: (
      <FaTint className="text-emerald-700 text-sm sm:text-base md:text-xl mr-1 md:mr-2" />
    ),
    badge: ["Combustível", "Industrial"],
    summary:
      "Álcool etílico hidratado entre 95,1% e 96,5% v/v — usado amplamente como biocombustível em motores flex e em aplicações industriais.",
    highlights: [
      { k: "Teor alcoólico", v: "95,1–96,5 % v/v" },
      { k: "pH", v: "6,0–8,0" },
      { k: "Condutividade", v: "≤ 300 µS/m" },
    ],
    notes: [
      "Conforme Resolução ANP nº 207/2022.",
      "Produto inflamável. Armazene em local ventilado e afastado de fontes de ignição.",
    ],
    table: {
      headers: ["Parâmetro", "Unidade", "Especificação", "Método"],
      rows: [
        ["Massa específica (20 °C)", "kg/m³", "805,2 a 811,2", "NBR 5992"],
        ["Teor alcoólico (°INPM)", "% m/m", "92,5 a 94,6", "NBR 5992"],
        ["Teor alcoólico (°GL)", "% v/v", "95,1 a 96,5", "NBR 5992"],
        ["pH", "—", "6,0 a 8,0", "NBR 10891"],
        ["Condutividade elétrica", "µS/m", "Máx. 300", "NBR 10547"],
        ["Acidez total", "mg/L", "Máx. 30", "NBR 9866"],
        ["Teor de metanol", "%", "Máx. 0,5", "ISO 1388-8"],
        ["Cor", "—", "Incolor", "Visual"],
        ["Aspecto", "—", "L.I.I. (Líquido, Isento de Impurezas)", "Visual"],
      ],
    },
  },
  {
    id: "etanol-anidro",
    name: "Etanol Anidro",
    icon: (
      <FaBurn className="text-emerald-700 text-sm sm:text-base md:text-xl mr-1 md:mr-2" />
    ),
    badge: ["Mistura com gasolina", "Combustível"],
    summary:
      "Álcool etílico anidro (≥ 99,3% m/m) destinado principalmente à mistura com gasolina, elevando a octanagem e reduzindo emissões.",
    highlights: [
      { k: "Teor alcoólico", v: "≥ 99,3 % m/m" },
      { k: "Massa esp. (20 °C)", v: "≤ 791,5 kg/m³" },
      { k: "Acidez total", v: "≤ 30 mg/L" },
    ],
    notes: [
      "Conforme Resolução ANP nº 207/2022.",
      "Produto inflamável. Manipular com EPI e aterramento adequado.",
    ],
    table: {
      headers: ["Parâmetro", "Unidade", "Especificação", "Método"],
      rows: [
        ["Massa específica (20 °C)", "kg/m³", "Máx. 791,5", "NBR 5992"],
        ["Teor alcoólico (°INPM)", "% m/m", "Mín. 99,3", "NBR 5992"],
        ["Condutividade elétrica", "µS/m", "Máx. 300", "NBR 10547"],
        ["Acidez total", "mg/L", "Máx. 30", "NBR 9866"],
        ["Teor de metanol", "%", "Máx. 0,5", "ISO 1388-8"],
        ["Cor", "—", "Incolor", "Visual"],
        ["Aspecto", "—", "L.I.I.", "Visual"],
      ],
    },
  },
  {
    id: "etanol-neutro",
    name: "Etanol Neutro",
    icon: (
      <FaFlask className="text-emerald-700 text-sm sm:text-base md:text-xl mr-1 md:mr-2" />
    ),
    badge: ["Cosmético", "Farmacêutico", "Alimentos"],
    summary:
      "Álcool neutro de alta pureza (≥ 96,1% v/v), baixo teor de impurezas e odor característico — indicado para cosméticos, fármacos e alimentos.",
    highlights: [
      { k: "Grau alcoólico", v: "≥ 96,1 % v/v" },
      { k: "Permanganato (20 °C)", v: "≥ 30 min" },
      { k: "Condutividade", v: "≤ 100 µS/m" },
    ],
    notes: [
      "Especificações internas do produto com referência a normas ABNT e boas práticas de qualidade.",
      "Armazenagem hermética, longe de luz e calor, devido ao caráter higroscópico.",
    ],
    table: {
      headers: ["Parâmetro", "Unidade", "Especificação"],
      rows: [
        ["Grau alcoólico (°GL)", "% v/v", "Mín. 96,1"],
        ["Grau alcoólico (°INPM)", "% m/m", "Mín. 94,0"],
        ["Massa específica a 20 °C", "kg/m³", "Máx. 807,1"],
        ["Acidez (ácido acético)", "mg/L", "Máx. 10"],
        ["pH", "—", "6,0 a 8,0"],
        ["Condutividade elétrica", "µS/m", "Máx. 100"],
        ["Teste de permanganato (20 °C)", "min", "Mín. 30"],
        ["Cor", "—", "Incolor"],
        ["Odor", "—", "Característico"],
        ["Acetaldeído", "mg/L", "Máx. 10"],
        ["Acetona", "mg/L", "Máx. 2,5"],
        ["Álcool metílico", "mg/L", "Máx. 10"],
        ["Acetato de etila", "mg/L", "Máx. 5"],
        ["Acetal", "mg/L", "Máx. 5"],
        ["Álcoois superiores (soma)", "mg/L", "Máx. 5"],
        ["N-Propanol", "mg/L", "N.E."],
        ["Isobutanol", "mg/L", "N.E."],
        ["N-Butanol", "mg/L", "N.E."],
        ["Álcool isoamílico", "mg/L", "N.E."],
        ["Aspecto", "—", "L.I.I.V.I.H"],
      ],
    },
  },
  {
    id: "cachaca",
    name: "Cachaça",
    icon: (
      <FaWineBottle className="text-emerald-700 text-sm sm:text-base md:text-xl mr-1 md:mr-2" />
    ),
    badge: ["Bebidas", "MAPA"],
    summary:
      "Aguardente de cana com graduação de 38% a 48% v/v; especificações conforme Normativa MAPA nº 13/2005.",
    highlights: [
      { k: "Grau alcoólico", v: "38–48 % v/v" },
      { k: "Álcoois superiores (soma)", v: "≤ 360 mg/100 mL AA" },
      { k: "Furfural", v: "≤ 5 mg/100 mL AA" },
    ],
    notes: ["Consumir de forma responsável. Para uso industrial/culinário, seguir as exigências legais vigentes."],
    table: {
      headers: ["Parâmetro", "Unidade", "Especificação"],
      rows: [
        ["Grau alcoólico (°GL)", "% v/v", "38,0 a 48,0"],
        ["pH", "—", "Não regulamentado (tipicamente 3,0 a 5,0)"],
        ["Condutividade elétrica", "µS/m", "Máx. 30"],
        ["Acidez volátil (ác. acético)", "mg/100 mL AA", "Máx. 150"],
        ["Acetaldeído", "mg/100 mL AA", "Máx. 30"],
        ["Álcool metílico", "mg/100 mL AA", "Máx. 20"],
        ["Acetato de etila", "mg/100 mL AA", "Máx. 200"],
        ["N-Propanol", "mg/100 mL AA", "N.E."],
        ["Isobutanol", "mg/100 mL AA", "N.E."],
        ["Álcool isoamílico", "mg/100 mL AA", "N.E."],
        ["Álcool n-amilico", "mg/100 mL AA", "N.E."],
        ["Álcoois superiores (soma)", "mg/100 mL AA", "Máx. 360"],
        ["Furfural", "mg/100 mL AA", "Máx. 5"],
        ["Aspecto", "—", "L.I.I."],
      ],
    },
  },
];

const PRODUCT_IMAGES = {
  "etanol-hidratado": "/images/etanol-hidratado.jpg",
  "etanol-anidro": "/images/etanol-anidro.jpg",
  "etanol-neutro": "/images/etanol-neutro.jpg",
  cachaca: "/images/cachaca.jpg",
};

// ---- Tabela de especificações ----
function SpecTable({ headers, rows }) {
  return (
    <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-emerald-200 via-teal-200 to-lime-200 shadow-sm">
      <div className="rounded-2xl bg-white/90 backdrop-blur-sm">
        <div className="relative overflow-x-auto">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-white/90 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-white/90 to-transparent" />
          <table className="min-w-full text-left align-middle">
            <thead className="bg-emerald-50/90 text-emerald-900 sticky top-0 z-10">
              <tr>
                {headers.map((h) => (
                  <th
                    key={h}
                    className="px-4 md:px-5 py-2.5 md:py-3 text-xs md:text-sm font-semibold border-b border-emerald-100"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-slate-800">
              {rows.map((r, idx) => (
                <tr
                  key={idx}
                  className="group odd:bg-white/70 even:bg-emerald-50/40 hover:bg-emerald-50/80 transition-colors"
                >
                  {r.map((c, i) => (
                    <td
                      key={i}
                      className="px-4 md:px-5 py-2.5 md:py-3 text-xs md:text-sm border-b border-emerald-100 align-top"
                    >
                      {i === 1 ? (
                        <code className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-800">
                          {c}
                        </code>
                      ) : i === 3 ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                          {c}
                        </span>
                      ) : (
                        c
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---- Destaques em linha (horizontal) ----
function InlineHighlights({ items = [] }) {
  return (
    <div className="mt-3 rounded-xl border border-emerald-100 bg-white/80 backdrop-blur-sm px-3 md:px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 md:gap-x-6 gap-y-2 text-xs md:text-sm">
        {items.map((h, i) => (
          <div key={h.k} className="inline-flex items-center gap-2">
            <span className="text-emerald-900 font-semibold">{h.k}:</span>
            <span className="text-slate-700 whitespace-nowrap tabular-nums">
              {h.v}
            </span>
            {i < items.length - 1 && (
              <span className="hidden sm:inline-block text-emerald-300">
                |
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Página ----
export default function ProdutosPage() {
  const [active, setActive] = useState(PRODUCTS[0]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Produtos — Usina Rosa";
  }, []);

  return (
    <main className="bg-white">
      {/* HERO */}
      <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-50" />
          <motion.div
            aria-hidden
            className="absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, rgba(16,185,129,0.35), transparent 60%)",
            }}
            initial={{ opacity: 0.6, scale: 0.95 }}
            animate={{ opacity: 0.9, scale: 1 }}
            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
          />
          <motion.div
            aria-hidden
            className="absolute -bottom-20 -left-16 w-96 h-96 rounded-full blur-[80px]"
            style={{
              background:
                "radial-gradient(circle, rgba(52,211,153,0.3), transparent 60%)",
            }}
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: 0.8, scale: 1.05 }}
            transition={{ duration: 5, repeat: Infinity, repeatType: "reverse" }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(16,185,129,0.06) 0px, rgba(16,185,129,0.06) 2px, transparent 2px, transparent 26px)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 via-teal-700 to-lime-700"
          >
            Portfólio de Produtos
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="mt-3 text-base md:text-lg text-slate-700 max-w-3xl mx-auto"
          >
            Especificações técnicas completas, controle de qualidade rigoroso e
            conformidade com normas — tudo alinhado à excelência Usina Rosa.
          </motion.p>
        </div>
        <div className="mx-auto mt-8 md:mt-10 h-px max-w-7xl bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
      </section>

      {/* CONTEÚDO PRINCIPAL */}
      <section className="px-4 md:px-6 pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Seleção MOBILE: carrossel de chips */}
          <div className="mb-4 lg:hidden">
            <p className="text-xs font-semibold text-emerald-900 mb-2">
              Selecione o produto:
            </p>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-transparent">
              {PRODUCTS.map((p) => {
                const isActive = active.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActive(p)}
                    className={`flex items-center whitespace-nowrap px-3 py-2 rounded-full border text-xs font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-md"
                        : "bg-white text-slate-800 border-emerald-200 hover:bg-emerald-50"
                    }`}
                  >
                    {p.icon}
                    <span>{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Tabs laterais - DESKTOP apenas (não mexi no comportamento web) */}
            <aside className="hidden lg:col-span-1 lg:sticky lg:top-28 lg:block self-start mb-4 lg:mb-0">
              <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-emerald-200 via-teal-200 to-lime-200 shadow-sm">
                <div
                  className="rounded-2xl bg-white/85 backdrop-blur-sm p-2.5 md:p-3"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(135deg, rgba(16,185,129,0.05) 0px, rgba(16,185,129,0.05) 2px, transparent 2px, transparent 26px)",
                  }}
                >
                  {PRODUCTS.map((p) => {
                    const isActive = active.id === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setActive(p)}
                        className="relative w-full text-left px-3 md:px-4 py-2.5 md:py-3 rounded-xl flex items-center justify-between mb-2 last:mb-0"
                      >
                        {isActive && (
                          <motion.span
                            layoutId="activeTab"
                            className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg"
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 30,
                            }}
                          />
                        )}
                        <span
                          className={`relative z-10 flex items-center font-medium text-sm md:text-base ${
                            isActive ? "text-white" : "text-slate-800"
                          }`}
                        >
                          {p.icon}
                          {p.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>

            {/* Painel do produto ativo */}
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.35 }}
                  className="rounded-3xl border border-emerald-100 bg-white/85 backdrop-blur-sm p-4 sm:p-6 md:p-8 shadow-md"
                >
                  <div className="-mt-4 sm:-mt-6 md:-mt-8 -mx-4 sm:-mx-6 md:-mx-8 mb-4 md:mb-6 h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-lime-500 rounded-t-3xl" />

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 items-start">
                    {/* Imagem + destaques */}
                    <div>
                      <div className="relative overflow-hidden rounded-xl border border-emerald-100 shadow-sm">
                        <img
                          src={PRODUCT_IMAGES[active.id] || "/images/bg-produtos.jpg"}
                          alt={active.name}
                          className="w-full h-40 sm:h-48 md:h-60 lg:h-64 object-cover"
                        />
                      </div>

                      {/* Destaques na horizontal */}
                      <InlineHighlights items={active.highlights} />

                      {/* Badges */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {active.badge.map((b) => (
                          <span
                            key={b}
                            className="px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold bg-emerald-100 text-emerald-800"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Título e descrição */}
                    <div className="lg:col-span-2">
                      <h2 className="text-xl md:text-3xl font-extrabold text-emerald-900 flex items-center">
                        {active.icon} <span className="ml-1">{active.name}</span>
                      </h2>
                      <p className="mt-2 text-sm md:text-lg text-slate-700 max-w-3xl">
                        {active.summary}
                      </p>
                      <div className="mt-4 h-px w-full bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
                    </div>
                  </div>

                  {/* Tabela técnica */}
                  <div className="mt-5 md:mt-6">
                    <SpecTable headers={active.table.headers} rows={active.table.rows} />
                  </div>

                  {/* Notas */}
                  {active.notes?.length ? (
                    <div className="mt-5 md:mt-6 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 md:p-4 text-emerald-900 flex items-start gap-3">
                      <FaInfoCircle className="mt-0.5 md:mt-1" />
                      <div className="text-xs md:text-sm leading-relaxed">
                        {active.notes.map((n, i) => (
                          <p key={i} className="mb-1 last:mb-0">
                            {n}
                          </p>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
